"use client";

import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker } from "react-day-picker";

import { cn } from "../../lib/utils";
import { buttonVariants } from "../button";
import { YearNavigation } from "./year-nav";

export type CalendarProps = React.ComponentProps<typeof DayPicker> & {
  yearRange?: {
    from: number;
    to: number;
  };
};

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  yearRange,
  ...props
}: CalendarProps) {
  const [displayedMonth, setDisplayedMonth] = React.useState<Date>(
    (props.selected as Date) || new Date()
  );

  const handleYearChange = (year: number) => {
    const newDate = new Date(displayedMonth);
    newDate.setFullYear(year);
    setDisplayedMonth(newDate);
  };

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("p-3", className)}
      month={displayedMonth}
      onMonthChange={setDisplayedMonth}
      classNames={{
        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
        month: "space-y-4",
        // caption: "flex justify-center pt-1 relative items-center",
        caption_label: "text-sm font-medium",
        nav: "space-x-1 flex items-center",
        nav_button: cn(
          buttonVariants({ variant: "outline" }),
          "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100"
        ),
        // nav_button_previous: "absolute left-1",
        // nav_button_next: "absolute right-1",
        table: "w-full border-collapse space-y-0",
        // head_row: "flex mt-2",
        // head_cell:
        //   "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
        // row: "flex w-full mt-3",
        // cell: "h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
        // day: cn(
        //   buttonVariants({ variant: "ghost" }),
        //   "h-9 w-9 p-0 font-normal aria-selected:opacity-100"
        // ),
        day_range_end: "day-range-end",
        // day_selected:
        //   "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
        // day_today: "bg-accent text-accent-foreground",
        day_outside:
          "day-outside text-muted-foreground aria-selected:bg-accent/50 aria-selected:text-muted-foreground",
        day_disabled: "text-muted-foreground opacity-50",
        day_range_middle:
          "aria-selected:bg-accent aria-selected:text-accent-foreground",
        day_hidden: "invisible",
        caption: "justify-start flex relative items-center",
        cell: "h-8 w-8 text-xs mr-1.25 font-medium last-of-type:mr-0",
        head_row: "flex",
        row: "flex w-full mt-2.5 first-of-type:mt-1",
        head_cell:
          "h-8 w-8 p-1 text-xs text-muted-foreground rounded-md font-normal mr-1 last-of-type:mr-0",
        day_selected:
          "relative bg-primary text-primary-foreground rounded-sm after:bg-background",
        day_today:
          "relative text-accent-foreground after:content-[''] after:absolute after:bottom-1 after:left-1/2 after:-translate-x-1/2 after:h-1 after:w-1 after:rounded-full after:bg-primary",
        day: "h-8 w-8 p-0 text-xs font-medium border border-transparent hover:border-border rounded-sm",
        nav_button_next: "absolute right-0 top-0 border-none shadow-none",
        nav_button_previous: "absolute right-8 top-0 border-none shadow-none",
        ...classNames,
      }}
      {...props}
      components={{
        IconLeft: ({ className, ...props }) => (
          <ChevronLeft className={cn("h-4 w-4", className)} {...props} />
        ),
        IconRight: ({ className, ...props }) => (
          <ChevronRight className={cn("h-4 w-4", className)} {...props} />
        ),
        CaptionLabel: (props) => {
          const { displayMonth } = props;
          const month = displayMonth.toLocaleString("default", {
            month: "long",
          });
          const year = displayMonth.getFullYear();
          return (
            <div className={cn("flex flex-row gap-1 px-1 items-center")}>
              <h5>{month}</h5>
              <YearNavigation
                displayedYear={year}
                yearRange={yearRange}
                onChange={handleYearChange}
              />
            </div>
          );
        },
        ...props.components,
      }}
    />
  );
}
Calendar.displayName = "Calendar";

export { Calendar };
